<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SgpAPI from 'dashboard/api/sgp';

// Props
defineProps({
  hasCustomer: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(['openDialog']);

// i18n
const { t: $t } = useI18n();

// General loading
const loading = ref(false);

// Metrics
const openServiceOrders = ref(null);
const activeOS = ref(null);
const openTitles = ref(null);
const suspendedCustomers = ref(null);

// Radius logs and maintenance items
const radiusLogs = ref([]);
const maintItems = ref([]);
const radiusFilter = ref('');
const maintFilter = ref('');
const radiusPage = ref(1);
const maintPage = ref(1);
const RADIUS_PAGE_SIZE = 20;
const MAINT_PAGE_SIZE = 10;

// Loading flags
const radiusLoading = ref(false);
const maintLoading = ref(false);

// Helper functions first to avoid no-use-before-define errors
function capitalizeWord(value) {
  if (!value) return '';
  const lower = value.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function cleanRadiusDetails(text) {
  if (!text) return '';
  let cleaned = text;

  cleaned = cleaned.replace(/\[[^\]]*\]/g, '').trim();
  cleaned = cleaned.replace(/\(from client[^)]*\)/gi, '').trim();
  cleaned = cleaned.replace(/\(did[^)]*\)/gi, '').trim();
  cleaned = cleaned.replace(/\(cli[^)]*\)/gi, '').trim();
  cleaned = cleaned.replace(/\(user[^)]*\)/gi, '').trim();
  cleaned = cleaned.replace(/\bdid\s+[^\s)]+/gi, '').trim();
  cleaned = cleaned.replace(/\bcli\s+[0-9A-Fa-f:.-]+/gi, '').trim();
  cleaned = cleaned.replace(/\buser\s+[^\s)]+/gi, '').trim();
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  cleaned = cleaned.replace(/^[:-]\s*/, '').trim();

  return cleaned;
}

function formatRadiusTimestamp(timestamp) {
  const normalized = String(timestamp || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return '';

  let candidate = normalized;
  const duplicateYear = normalized.match(
    /^(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})\s+(\d{4})$/
  );
  if (duplicateYear) {
    candidate = `${duplicateYear[1]} ${duplicateYear[2]}`;
  }

  let parsed = new Date(candidate);

  if (Number.isNaN(parsed.getTime()) && duplicateYear) {
    const isoCandidate = `${duplicateYear[1].replace(' ', 'T')}Z`;
    parsed = new Date(isoCandidate);
  }

  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }

  try {
    return parsed.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch (_) {
    return normalized;
  }
}

// Main parsing function that uses the helper functions above
function parseRadiusLogText(raw) {
  const base = {
    type: 'Log',
    summary: raw,
    details: '',
    code: null,
    timestampRaw: '',
    timestampDisplay: '',
  };

  if (!raw) {
    return base;
  }

  const match = raw.match(
    /^(?<timestamp>.+?)\s+:\s+(?<type>[A-Za-z]+)\s*:\s*(?<rest>.*)$/
  );
  if (!match) {
    return {
      ...base,
      summary: raw.trim(),
    };
  }

  const timestamp = (match.groups.timestamp || '').trim();
  const type = (match.groups.type || '').trim();
  const rest = (match.groups.rest || '').trim();

  let summaryPart = rest;
  let detailPart = '';

  const colonIndex = rest.indexOf(': ');
  if (colonIndex !== -1) {
    summaryPart = rest.slice(0, colonIndex).trim();
    detailPart = rest.slice(colonIndex + 2).trim();
  }

  let code = null;
  const codeMatch = summaryPart.match(/^\(([^)]+)\)\s*(.*)$/);
  if (codeMatch) {
    const maybeCode = codeMatch[1].trim();
    if (maybeCode) {
      code = maybeCode;
    }
    summaryPart = (codeMatch[2] || '').trim();
  }

  const inlineDetails = [];
  summaryPart = summaryPart
    .replace(/\(([^)]+)\)/g, (_, group) => {
      const cleaned = group.trim();
      if (cleaned) {
        inlineDetails.push(cleaned);
      }
      return '';
    })
    .trim();
  summaryPart = summaryPart.replace(/\(\)/g, '').trim();

  let aggregatedDetails = [inlineDetails.join(' '), detailPart]
    .filter(Boolean)
    .join(' ')
    .trim();
  aggregatedDetails = cleanRadiusDetails(aggregatedDetails);

  if (!summaryPart && aggregatedDetails) {
    summaryPart = aggregatedDetails;
    aggregatedDetails = '';
  }

  return {
    type: type ? capitalizeWord(type) : 'Log',
    summary: summaryPart || rest || raw,
    details: aggregatedDetails,
    code,
    timestampRaw: timestamp,
    timestampDisplay: formatRadiusTimestamp(timestamp) || timestamp,
  };
}

function inferRadiusSeverity(parsed, color, raw) {
  const haystack = `${parsed.type} ${parsed.summary} ${parsed.details} ${raw}`
    .toLowerCase()
    .trim();
  const palette = String(color || '').toLowerCase();

  if (/incorrect|denied|reject|fail|error|timeout|invalid/.test(haystack)) {
    return 'error';
  }
  if (/warning|slow|delay/.test(haystack)) {
    return 'warning';
  }
  if (/login ok|released ip|success|accept/.test(haystack)) {
    return 'success';
  }
  if (palette.includes('3eaa3e')) {
    return 'success';
  }
  return 'info';
}

function radiusIndicatorClass(variant) {
  if (variant === 'success') return 'bg-n-teal-8';
  if (variant === 'error') return 'bg-n-ruby-8';
  if (variant === 'warning') return 'bg-n-amber-8';
  return 'bg-n-blue-8';
}

function extractRadiusMetadata(raw, parsed = {}) {
  if (!raw) return [];

  const meta = [];
  const seen = new Set();
  const pushMeta = (label, value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return;
    const key = `${label}:${trimmed}`;
    if (seen.has(key)) return;
    seen.add(key);
    meta.push({ label, value: trimmed });
  };

  const bracketMatch = raw.match(/\[([^\]]+)\]/);
  if (bracketMatch && bracketMatch[1]) {
    const content = bracketMatch[1].trim();
    if (content) {
      const slashIndex = content.indexOf('/');
      const userValue =
        slashIndex !== -1 ? content.slice(0, slashIndex).trim() : content;
      pushMeta('User', userValue);

      const authTypeMatch = content.match(/Auth-Type\s*=\s*([A-Za-z0-9_-]+)/i);
      if (authTypeMatch) {
        pushMeta('Auth-Type', authTypeMatch[1]);
      }
    }
  }

  const clientMatch = raw.match(/from client\s+([^\s)]+)/i);
  pushMeta('Client', clientMatch && clientMatch[1]);

  const portMatch = raw.match(/\bport\s+(\d+)/i);
  pushMeta('Port', portMatch && portMatch[1]);

  const cliMatch = raw.match(/\bcli\s+([0-9A-Fa-f:.-]+)/i);
  const cliValue =
    cliMatch && cliMatch[1] ? cliMatch[1].replace(/[),]$/, '') : null;
  pushMeta('CLI', cliValue);

  const userWordMatch = raw.match(/\buser\s+([^\s)]+)/i);
  pushMeta('User', userWordMatch && userWordMatch[1]);

  const releasedIpMatch = raw.match(/Released IP\s+([0-9.]+)/i);
  pushMeta('IP', releasedIpMatch && releasedIpMatch[1]);

  const genericIpMatch = raw.match(/\bIP\s+([0-9.]+)/i);
  if (
    genericIpMatch &&
    (!releasedIpMatch || releasedIpMatch[1] !== genericIpMatch[1])
  ) {
    pushMeta('IP', genericIpMatch[1]);
  }

  const didMatch = raw.match(/\bdid\s+([^\s)]+)/i);
  if (didMatch && didMatch[1]) {
    const didValue = didMatch[1].trim();
    if (didValue && didValue.toLowerCase() !== 'cli') {
      pushMeta('DID', didValue);
    }
  }

  if (parsed?.code) {
    pushMeta('ID', parsed.code);
  }

  return meta;
}

function formatMaintenanceDate(dateStr) {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return dateStr;
  }
}

function normalizeMaintenanceSeverity(severity) {
  const normalized = String(severity || '').toLowerCase();
  const severityMap = {
    'indisponibilidade total': 'critical',
    'indisponibilidade parcial': 'high',
    'problemas de performance': 'medium',
    operacional: 'low',
    desconhecido: 'unknown',
  };
  return severityMap[normalized] || 'unknown';
}

function normalizeMaintenanceStatus(status) {
  const normalized = String(status || '').toLowerCase();
  const statusMap = {
    identificado: 'identified',
    investigando: 'investigating',
    observando: 'monitoring',
    resolvido: 'resolved',
    desconhecido: 'unknown',
  };
  return statusMap[normalized] || 'unknown';
}

function inferMaintenanceCategory(description, observations) {
  const text = `${description} ${observations}`.toLowerCase();

  if (/rompimento|fogo|poste|backbone/i.test(text)) return 'emergency';
  if (/splitter|amplia|troca|preventiva/i.test(text)) return 'maintenance';
  if (/cto|fibra|cabo|óptic/i.test(text)) return 'infrastructure';
  if (/area|região/i.test(text)) return 'regional';

  return 'general';
}

function extractAffectedResources(entry) {
  const resources = [];

  if (entry.pops?.length) {
    resources.push({
      type: 'POP',
      count: entry.pops.length,
      value: entry.pops.map(p => `${p.cidade}/${p.uf}`).join(', '),
    });
  }

  if (entry.ctos?.length) {
    resources.push({
      type: 'CTO',
      count: entry.ctos.length,
      value: entry.ctos.map(c => c.nome).join(', '),
    });
  }

  if (entry.nas?.length) {
    resources.push({
      type: 'NAS',
      count: entry.nas.length,
      value: entry.nas.map(n => n.nome || n.descricao).join(', '),
    });
  }

  if (entry.olts?.length) {
    resources.push({
      type: 'OLT',
      count: entry.olts.length,
      value: entry.olts.map(o => o.nome).join(', '),
    });
  }

  if (entry.oltpons?.length) {
    resources.push({
      type: 'PON',
      count: entry.oltpons.length,
      value: entry.oltpons
        .map(p => `${p.olt_nome} ${p.slot}/${p.pon}`)
        .join(', '),
    });
  }

  return resources;
}

function calculateMaintenanceDuration(startDate, endDate) {
  if (!startDate || !endDate) return null;
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
      return null;

    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) return '< 1h';
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    const remainingHours = diffHours % 24;
    return remainingHours > 0
      ? `${diffDays}d ${remainingHours}h`
      : `${diffDays}d`;
  } catch (_) {
    return null;
  }
}

function inferMaintenanceUrgency(description, observations, severity) {
  const text = `${description} ${observations}`.toLowerCase();
  const sev = String(severity || '').toLowerCase();

  if (sev.includes('total') || /emergencial|fogo|rompimento/i.test(text))
    return 'critical';
  if (sev.includes('parcial') || /urgente|problema/i.test(text)) return 'high';
  if (/preventiva|amplia|troca/i.test(text)) return 'low';

  return 'medium';
}

function normalizeRadiusLogEntry(entry) {
  const rawText =
    typeof entry === 'string'
      ? entry
      : entry?.log || entry?.message || entry?.descricao || '';
  const color =
    typeof entry === 'object' && entry?.color ? String(entry.color) : '';
  const trimmed = String(rawText || '').trim();

  const parsed = parseRadiusLogText(trimmed);
  const metadata = extractRadiusMetadata(trimmed, parsed);
  const severity = inferRadiusSeverity(parsed, color, trimmed);
  const indicatorClass = radiusIndicatorClass(severity);

  const metadataText = metadata
    .map(item => `${item.label} ${item.value}`)
    .join(' ');

  const searchPieces = [
    trimmed,
    parsed.type,
    parsed.summary,
    parsed.details,
    parsed.timestampDisplay,
    metadataText,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return {
    ...parsed,
    metadata,
    metadataText,
    rawText: trimmed,
    badgeVariant: severity,
    indicatorClass,
    searchText: searchPieces,
  };
}

function normalizeMaintenanceEntry(entry) {
  const description = entry.descricao || '';
  const observations = entry.observacoes || '';
  const startDate = formatMaintenanceDate(entry.data_inicial);
  const endDate = formatMaintenanceDate(entry.data_final);

  // Manter datas originais para ordenação
  const startDateRaw = entry.data_inicial ? new Date(entry.data_inicial) : null;
  const endDateRaw = entry.data_final ? new Date(entry.data_final) : null;

  const severity = normalizeMaintenanceSeverity(entry.severidade);
  const status = normalizeMaintenanceStatus(entry.status);
  const category = inferMaintenanceCategory(description, observations);
  const affectedResources = extractAffectedResources(entry);

  const searchPieces = [
    description,
    observations,
    entry.severidade,
    entry.status,
    startDate,
    endDate,
    affectedResources.map(r => r.value).join(' '),
    category,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return {
    ...entry,
    normalizedDescription: description,
    normalizedObservations: observations,
    startDate,
    endDate,
    startDateRaw,
    endDateRaw,
    severity,
    status,
    category,
    affectedResources,
    searchText: searchPieces,
    duration: calculateMaintenanceDuration(
      entry.data_inicial,
      entry.data_final
    ),
    urgencyLevel: inferMaintenanceUrgency(
      description,
      observations,
      entry.severidade
    ),
  };
}

const normalizedRadiusLogs = computed(() =>
  radiusLogs.value.map(entry => normalizeRadiusLogEntry(entry))
);

const filteredRadiusLogs = computed(() => {
  const term = radiusFilter.value.trim().toLowerCase();
  if (!term) return normalizedRadiusLogs.value;
  return normalizedRadiusLogs.value.filter(entry =>
    entry.searchText.includes(term)
  );
});

const radiusTotalPages = computed(() => {
  const total = filteredRadiusLogs.value.length;
  return total ? Math.ceil(total / RADIUS_PAGE_SIZE) : 1;
});

const paginatedRadiusLogs = computed(() => {
  const start = (radiusPage.value - 1) * RADIUS_PAGE_SIZE;
  return filteredRadiusLogs.value.slice(start, start + RADIUS_PAGE_SIZE);
});

const normalizedMaintItems = computed(() =>
  maintItems.value
    .map(entry => normalizeMaintenanceEntry(entry))
    .sort((a, b) => {
      // Ordenar por data inicial mais recente primeiro
      const dateA = a.startDateRaw || new Date(0); // Fallback para data muito antiga
      const dateB = b.startDateRaw || new Date(0);
      return dateB - dateA; // Descending order (mais recente primeiro)
    })
);

const filteredMaintItems = computed(() => {
  const term = maintFilter.value.trim().toLowerCase();
  if (!term) return normalizedMaintItems.value;
  return normalizedMaintItems.value.filter(entry =>
    entry.searchText.includes(term)
  );
});

const maintTotalPages = computed(() => {
  const total = filteredMaintItems.value.length;
  return total ? Math.ceil(total / MAINT_PAGE_SIZE) : 1;
});

const paginatedMaintenance = computed(() => {
  const start = (maintPage.value - 1) * MAINT_PAGE_SIZE;
  return filteredMaintItems.value.slice(start, start + MAINT_PAGE_SIZE);
});

const radiusPaginationInfo = computed(() => {
  const total = filteredRadiusLogs.value.length;
  if (!total) {
    return { firstIndex: 0, lastIndex: 0, totalCount: 0 };
  }
  const firstIndex = (radiusPage.value - 1) * RADIUS_PAGE_SIZE + 1;
  const lastIndex = Math.min(total, radiusPage.value * RADIUS_PAGE_SIZE);
  return { firstIndex, lastIndex, totalCount: total };
});

const maintPaginationInfo = computed(() => {
  const total = filteredMaintItems.value.length;
  if (!total) {
    return { firstIndex: 0, lastIndex: 0, totalCount: 0 };
  }
  const firstIndex = (maintPage.value - 1) * MAINT_PAGE_SIZE + 1;
  const lastIndex = Math.min(total, maintPage.value * MAINT_PAGE_SIZE);
  return { firstIndex, lastIndex, totalCount: total };
});

function getBadgeClasses(type, value) {
  const baseClasses =
    'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';

  if (type === 'status') {
    // Status badges - usando cores do sistema
    const statusColors = {
      ativo: 'bg-n-teal-3 text-n-teal-11',
      planejado: 'bg-n-blue-3 text-n-blue-11',
      'em andamento': 'bg-n-amber-3 text-n-amber-11',
      concluido: 'bg-n-teal-3 text-n-teal-11',
      cancelado: 'bg-n-slate-3 text-n-slate-11',
    };
    return `${baseClasses} ${statusColors[value?.toLowerCase()] || 'bg-n-slate-3 text-n-slate-11'}`;
  }

  if (type === 'severity') {
    // Severity badges - usando cores para urgência
    const severityColors = {
      baixa: 'bg-n-teal-3 text-n-teal-11',
      media: 'bg-n-amber-3 text-n-amber-11',
      alta: 'bg-n-ruby-3 text-n-ruby-11',
      critica: 'bg-n-ruby-6 text-white',
    };
    return `${baseClasses} ${severityColors[value?.toLowerCase()] || 'bg-n-slate-3 text-n-slate-11'}`;
  }

  if (type === 'log') {
    // Log type badges
    const logColors = {
      error: 'bg-n-ruby-3 text-n-ruby-11',
      warning: 'bg-n-amber-3 text-n-amber-11',
      info: 'bg-n-blue-3 text-n-blue-11',
      success: 'bg-n-teal-3 text-n-teal-11',
    };
    return `${baseClasses} ${logColors[value?.toLowerCase()] || 'bg-n-slate-3 text-n-slate-11'}`;
  }

  if (type === 'maintenance-status') {
    // Maintenance status badges
    const statusColors = {
      identified: 'bg-n-blue-3 text-n-blue-11',
      investigating: 'bg-n-amber-3 text-n-amber-11',
      monitoring: 'bg-n-teal-3 text-n-teal-11',
      resolved: 'bg-n-teal-3 text-n-teal-11',
      unknown: 'bg-n-slate-3 text-n-slate-11',
    };
    return `${baseClasses} ${statusColors[value?.toLowerCase()] || 'bg-n-slate-3 text-n-slate-11'}`;
  }

  if (type === 'maintenance-severity') {
    // Maintenance severity badges
    const severityColors = {
      critical: 'bg-n-ruby-6 text-white',
      high: 'bg-n-ruby-3 text-n-ruby-11',
      medium: 'bg-n-amber-3 text-n-amber-11',
      low: 'bg-n-teal-3 text-n-teal-11',
      unknown: 'bg-n-slate-3 text-n-slate-11',
    };
    return `${baseClasses} ${severityColors[value?.toLowerCase()] || 'bg-n-slate-3 text-n-slate-11'}`;
  }

  if (type === 'maintenance-category') {
    // Maintenance category badges
    const categoryColors = {
      emergency: 'bg-n-ruby-3 text-n-ruby-11',
      maintenance: 'bg-n-blue-3 text-n-blue-11',
      infrastructure: 'bg-n-amber-3 text-n-amber-11',
      regional: 'bg-n-teal-3 text-n-teal-11',
      general: 'bg-n-slate-3 text-n-slate-11',
    };
    return `${baseClasses} ${categoryColors[value?.toLowerCase()] || 'bg-n-slate-3 text-n-slate-11'}`;
  }

  if (type === 'maintenance-urgency') {
    // Maintenance urgency badges
    const urgencyColors = {
      critical: 'bg-n-ruby-6 text-white',
      high: 'bg-n-ruby-3 text-n-ruby-11',
      medium: 'bg-n-amber-3 text-n-amber-11',
      low: 'bg-n-teal-3 text-n-teal-11',
    };
    return `${baseClasses} ${urgencyColors[value?.toLowerCase()] || 'bg-n-slate-3 text-n-slate-11'}`;
  }

  if (type === 'resource') {
    // Resource type badges
    const resourceColors = {
      POP: 'bg-n-violet-3 text-n-violet-11',
      CTO: 'bg-n-blue-3 text-n-blue-11',
      NAS: 'bg-n-teal-3 text-n-teal-11',
      OLT: 'bg-n-amber-3 text-n-amber-11',
      PON: 'bg-n-iris-3 text-n-iris-11',
    };
    return `${baseClasses} ${resourceColors[value?.toUpperCase()] || 'bg-n-slate-3 text-n-slate-11'}`;
  }

  return `${baseClasses} bg-n-slate-3 text-n-slate-11`;
}

async function fetchMetrics() {
  try {
    loading.value = true;
    const [activeRes, suspendedRes, titlesRes, openOrdersRes] =
      await Promise.all([
        SgpAPI.listActiveServiceOrders({ status: '0', limit: '1' }),
        SgpAPI.listCustomers({ limit: '1' }),
        SgpAPI.listOpenTitles({
          status: 'abertos',
          limit: '1',
        }),
        SgpAPI.listServiceOrders({
          status: 0,
          limit: '1',
        }),
      ]);

    // Active OS
    activeOS.value = Array.isArray(activeRes.data?.ocorrencias)
      ? activeRes.data.ocorrencias.length
      : Number(activeRes.data?.paginacao?.total || 0);

    // Suspended customers
    suspendedCustomers.value = Array.isArray(suspendedRes.data)
      ? suspendedRes.data.length
      : Number(suspendedRes.data?.paginacao?.total || 0);

    // Open titles
    openTitles.value = Number(titlesRes.data?.paginacao?.total || 0);

    // Open service orders
    const openOrdersData = openOrdersRes.data || {};
    if (typeof openOrdersData?.paginacao?.total === 'number') {
      openServiceOrders.value = openOrdersData.paginacao.total;
    } else if (Array.isArray(openOrdersData?.ordens_servicos)) {
      openServiceOrders.value = openOrdersData.ordens_servicos.length;
    } else if (Array.isArray(openOrdersData)) {
      openServiceOrders.value = openOrdersData.length;
    } else {
      openServiceOrders.value = 0;
    }
  } catch (e) {
    // Keep placeholders if request fails
  } finally {
    loading.value = false;
  }
}

async function fetchRadiusLogs() {
  try {
    radiusLoading.value = true;
    const { data } = await SgpAPI.radiusLogs({ limit: 100 });
    if (Array.isArray(data)) {
      radiusLogs.value = data;
    } else if (Array.isArray(data?.logs)) {
      radiusLogs.value = data.logs;
    } else {
      radiusLogs.value = [];
    }
  } catch (e) {
    radiusLogs.value = [];
  } finally {
    radiusLoading.value = false;
  }
}

async function fetchMaintenance() {
  try {
    maintLoading.value = true;
    const { data } = await SgpAPI.maintenanceList({ limit: 50 });
    if (Array.isArray(data)) {
      maintItems.value = data;
    } else if (Array.isArray(data?.items)) {
      maintItems.value = data.items;
    } else {
      maintItems.value = [];
    }
  } catch (e) {
    maintItems.value = [];
  } finally {
    maintLoading.value = false;
  }
}

function openDialog(action) {
  emit('openDialog', action);
}

onMounted(() => {
  fetchMetrics();
  fetchRadiusLogs();
  fetchMaintenance();
});

watch(radiusFilter, () => {
  radiusPage.value = 1;
});

watch(maintFilter, () => {
  maintPage.value = 1;
});

watch(filteredRadiusLogs, () => {
  if (radiusPage.value > radiusTotalPages.value) {
    radiusPage.value = radiusTotalPages.value;
  }
});

watch(filteredMaintItems, () => {
  if (maintPage.value > maintTotalPages.value) {
    maintPage.value = maintTotalPages.value;
  }
});

watch(radiusLogs, () => {
  radiusPage.value = 1;
});

watch(maintItems, () => {
  maintPage.value = 1;
});

watch(maintTotalPages, total => {
  if (maintPage.value > total) {
    maintPage.value = total;
  }
});
</script>

<template>
  <div class="flex flex-col">
    <div
      class="px-4 py-3 border-b border-n-weak bg-n-solid-2/30 flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
    >
      <div class="flex flex-col gap-0.5">
        <span class="text-sm font-medium text-n-slate-12">{{
          $t('SGP.QUICK_ACTIONS')
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          hasCustomer
            ? $t('SGP.QUICK_ACTIONS_HINT_ACTIVE')
            : $t('SGP.QUICK_ACTIONS_HINT')
        }}</span>
      </div>
      <div class="flex flex-wrap gap-2 md:justify-end">
        <Button
          variant="outline"
          size="sm"
          color="blue"
          icon="i-lucide-globe"
          :label="$t('SGP.ACTIONS.VIABILITY_CEP')"
          @click="openDialog('cep')"
        />
        <Button
          variant="outline"
          size="sm"
          color="blue"
          icon="i-lucide-link"
          :label="$t('SGP.ACTIONS.VIABILITY_CTO')"
          @click="openDialog('cto')"
        />
        <Button
          variant="outline"
          size="sm"
          color="blue"
          icon="i-lucide-file-text"
          :label="$t('SGP.ACTIONS.PRE_REGISTRATION')"
          @click="openDialog('pre')"
        />
      </div>
    </div>

    <div
      class="grid grid-cols-2 md:grid-cols-4 gap-3 px-4 py-3 border-b border-n-weak bg-gradient-to-r from-n-surface-1 to-n-solid-2/20"
    >
      <div
        class="bg-n-surface-1 rounded-lg p-2.5 border border-n-weak hover:shadow-sm transition-shadow"
      >
        <p class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1">
          {{ $t('SGP.METRICS.OPEN_OS') }}
        </p>
        <p class="text-xl font-bold text-n-slate-12 text-center">
          {{ openServiceOrders ?? '—' }}
        </p>
      </div>
      <div
        class="bg-n-surface-1 rounded-lg p-2.5 border border-n-weak hover:shadow-sm transition-shadow"
      >
        <p class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1">
          {{ $t('SGP.METRICS.ACTIVE_OS') }}
        </p>
        <p class="text-xl font-bold text-n-slate-12 text-center">
          {{ activeOS ?? '—' }}
        </p>
      </div>
      <div
        class="bg-n-surface-1 rounded-lg p-2.5 border border-n-weak hover:shadow-sm transition-shadow"
      >
        <p class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1">
          {{ $t('SGP.METRICS.OPEN_TITLES') }}
        </p>
        <p class="text-xl font-bold text-n-slate-12 text-center">
          {{ openTitles ?? '—' }}
        </p>
      </div>
      <div
        class="bg-n-surface-1 rounded-lg p-2.5 border border-n-weak hover:shadow-sm transition-shadow"
      >
        <p class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1">
          {{ $t('SGP.METRICS.SUSPENDED_CUSTOMERS') }}
        </p>
        <p class="text-xl font-bold text-n-slate-12 text-center">
          {{ suspendedCustomers ?? '—' }}
        </p>
      </div>
    </div>

    <!-- Logs + Maintenance -->
    <div
      class="px-4 py-3 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 border-t border-n-weak bg-gradient-to-br from-n-solid-2/10 to-n-solid-2/5"
    >
      <div
        class="rounded-lg border border-n-weak bg-n-surface-1 shadow-sm hover:shadow-md transition-shadow flex flex-col min-w-0 max-h-96"
      >
        <div
          class="flex flex-col gap-2 px-4 py-3 border-b border-n-weak bg-n-solid-2/50 md:flex-row md:items-center md:justify-between"
        >
          <div class="flex items-center gap-2 justify-between w-full md:w-auto">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('SGP.RADIUS_LOGS.TITLE') }}
            </h3>
            <Button
              variant="link"
              size="xs"
              color="blue"
              :label="$t('SGP.RADIUS_LOGS.REFRESH')"
              @click="fetchRadiusLogs"
            />
          </div>
          <div class="relative w-full md:w-64">
            <div
              class="relative flex items-center gap-2 w-full rounded-lg px-2 hover:bg-n-alpha-1 border border-n-weak bg-n-surface-1"
            >
              <Icon
                icon="i-lucide-search"
                class="size-4 text-n-slate-10 flex-shrink-0"
              />
              <input
                v-model="radiusFilter"
                class="flex-1 py-2 text-sm font-medium leading-5 text-n-slate-12 placeholder:text-n-slate-10 bg-transparent border-0 focus:outline-none focus:ring-0"
                :placeholder="$t('SGP.RADIUS_LOGS.FILTER_PLACEHOLDER')"
              />
            </div>
          </div>
        </div>
        <div v-if="radiusLoading" class="px-3 py-2">
          <LoadingState :message="$t('SGP.LOADING')" />
        </div>
        <div v-else class="px-3 py-2 flex flex-col gap-3 flex-1 min-h-0">
          <div class="overflow-y-auto overflow-x-auto flex-1 min-h-0">
            <table class="w-full text-left text-sm min-w-[480px]">
              <thead class="text-n-slate-11">
                <tr>
                  <th class="px-2 py-2 w-[80px]">
                    {{ $t('SGP.RADIUS_LOGS.TYPE') }}
                  </th>
                  <th class="px-2 py-2">
                    {{ $t('SGP.RADIUS_LOGS.LOG') }}
                  </th>
                  <th class="px-2 py-2 w-[160px]">
                    {{ $t('SGP.RADIUS_LOGS.DATETIME') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!filteredRadiusLogs.length">
                  <td colspan="3" class="px-2 py-8 text-n-slate-11 text-center">
                    {{ $t('SGP.RADIUS_LOGS.EMPTY') }}
                  </td>
                </tr>
                <tr
                  v-for="(entry, idx) in paginatedRadiusLogs"
                  :key="idx"
                  class="border-t border-n-weak hover:bg-n-solid-2/50"
                >
                  <td class="px-2 py-2">
                    <div class="flex items-center gap-2">
                      <span
                        class="inline-flex h-2.5 w-2.5 rounded-full"
                        :class="[entry.indicatorClass]"
                      />
                      <span :class="getBadgeClasses('log', entry.badgeVariant)">
                        {{ entry.type || 'Log' }}
                      </span>
                    </div>
                  </td>
                  <td class="px-2 py-2 text-xs text-n-slate-12">
                    <div class="flex flex-col gap-1">
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ entry.summary || entry.rawText }}
                      </span>
                      <p
                        v-if="entry.details"
                        class="text-[11px] text-n-slate-11 leading-snug whitespace-pre-wrap break-words"
                      >
                        {{ entry.details }}
                      </p>
                      <div
                        v-if="entry.metadata.length"
                        class="flex flex-wrap gap-1"
                      >
                        <span
                          v-for="meta in entry.metadata"
                          :key="`${idx}-${meta.label}-${meta.value}`"
                          class="inline-flex items-center gap-1 rounded border border-n-weak bg-n-solid-2 px-1.5 py-0.5 text-[11px] font-mono text-n-slate-11"
                        >
                          <span class="uppercase text-[10px] text-n-slate-10">
                            {{ meta.label }}
                          </span>
                          <span class="text-n-slate-12">{{ meta.value }}</span>
                        </span>
                      </div>
                    </div>
                  </td>
                  <td
                    class="px-2 py-2 text-xs text-n-slate-11 whitespace-nowrap"
                  >
                    {{ entry.timestampDisplay || entry.timestampRaw || '—' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="filteredRadiusLogs.length"
            class="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-xs text-n-slate-11 border-t border-n-weak bg-n-solid-2/50"
          >
            <span>{{
              $t('GENERAL.SHOWING_RESULTS', radiusPaginationInfo)
            }}</span>
            <div class="flex items-center gap-2">
              <Button
                variant="outline"
                size="xs"
                color="slate"
                :label="$t('SGP.RADIUS_LOGS.PREV_PAGE')"
                :disabled="radiusPage === 1"
                @click="radiusPage = Math.max(1, radiusPage - 1)"
              />
              <span>
                {{ radiusPage }} {{ $t('SGP.SERVICE_ORDERS.OF') }}
                {{ radiusTotalPages }}
              </span>
              <Button
                variant="outline"
                size="xs"
                color="slate"
                :label="$t('SGP.RADIUS_LOGS.NEXT_PAGE')"
                :disabled="radiusPage === radiusTotalPages"
                @click="radiusPage = Math.min(radiusTotalPages, radiusPage + 1)"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        class="rounded-lg border border-n-weak bg-n-surface-1 shadow-sm hover:shadow-md transition-shadow flex flex-col min-w-0 max-h-96"
      >
        <div
          class="flex flex-col gap-2 px-4 py-3 border-b border-n-weak bg-n-solid-2/50 md:flex-row md:items-center md:justify-between"
        >
          <div class="flex items-center gap-2 justify-between w-full md:w-auto">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('SGP.MAINTENANCE.TITLE') }}
            </h3>
            <Button
              variant="link"
              size="xs"
              color="blue"
              :label="$t('SGP.MAINTENANCE.REFRESH')"
              @click="fetchMaintenance"
            />
          </div>
          <div class="relative w-full md:w-64">
            <div
              class="relative flex items-center gap-2 w-full rounded-lg px-2 hover:bg-n-alpha-1 border border-n-weak bg-n-surface-1"
            >
              <Icon
                icon="i-lucide-search"
                class="size-4 text-n-slate-10 flex-shrink-0"
              />
              <input
                v-model="maintFilter"
                class="flex-1 py-2 text-sm font-medium leading-5 text-n-slate-12 placeholder:text-n-slate-10 bg-transparent border-0 focus:outline-none focus:ring-0"
                :placeholder="$t('SGP.MAINTENANCE.FILTER_PLACEHOLDER')"
              />
            </div>
          </div>
        </div>
        <div v-if="maintLoading" class="px-3 py-2">
          <LoadingState :message="$t('SGP.LOADING')" />
        </div>
        <div v-else class="px-3 py-2 flex flex-col gap-3 flex-1 min-h-0">
          <div
            v-if="!filteredMaintItems.length"
            class="text-center py-8 text-xs text-n-slate-11"
          >
            {{
              maintFilter
                ? $t('SGP.MAINTENANCE.NO_RESULTS')
                : $t('SGP.MAINTENANCE.EMPTY')
            }}
          </div>
          <div v-else class="overflow-y-auto flex-1 min-h-0">
            <div
              v-for="(m, idx) in paginatedMaintenance"
              :key="m.id || idx"
              class="py-2.5 border-b border-n-weak last:border-b-0 hover:bg-n-solid-2/30 rounded px-2"
            >
              <!-- Header with status, severity and dates -->
              <div class="flex items-start justify-between mb-2">
                <div class="flex flex-wrap items-center gap-2">
                  <span
                    :class="getBadgeClasses('maintenance-status', m.status)"
                  >
                    {{ m.status }}
                  </span>
                  <span
                    :class="getBadgeClasses('maintenance-severity', m.severity)"
                  >
                    {{ m.severity }}
                  </span>
                  <span
                    v-if="m.category"
                    :class="getBadgeClasses('maintenance-category', m.category)"
                  >
                    {{ m.category }}
                  </span>
                  <span
                    v-if="m.duration"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-n-slate-3 text-n-slate-11"
                  >
                    {{ m.duration }}
                  </span>
                </div>
                <div class="text-xs text-n-slate-11 text-right">
                  <div>{{ m.startDate }}</div>
                  <div v-if="m.endDate">{{ m.endDate }}</div>
                </div>
              </div>

              <!-- Description -->
              <div v-if="m.normalizedDescription" class="mb-2">
                <h4 class="text-sm font-medium text-n-slate-12 leading-snug">
                  {{ m.normalizedDescription }}
                </h4>
              </div>

              <!-- Observations -->
              <div v-if="m.normalizedObservations" class="mb-2">
                <p class="text-xs text-n-slate-11 leading-snug">
                  {{ m.normalizedObservations }}
                </p>
              </div>

              <!-- Affected Resources -->
              <div
                v-if="m.affectedResources.length"
                class="flex flex-wrap gap-1"
              >
                <span
                  v-for="resource in m.affectedResources"
                  :key="`${m.id}-${resource.type}-${resource.count}`"
                  :class="getBadgeClasses('resource', resource.type)"
                  :title="resource.value"
                >
                  {{ resource.count }}{{ resource.type }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-if="filteredMaintItems.length"
            class="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-xs text-n-slate-11 border-t border-n-weak bg-n-solid-2/50"
          >
            <span>{{
              $t('GENERAL.SHOWING_RESULTS', maintPaginationInfo)
            }}</span>
            <div class="flex items-center gap-2">
              <Button
                variant="outline"
                size="xs"
                color="slate"
                :label="$t('SGP.MAINTENANCE.PREV_PAGE')"
                :disabled="maintPage === 1"
                @click="maintPage = Math.max(1, maintPage - 1)"
              />
              <span>
                {{ maintPage }} {{ $t('SGP.SERVICE_ORDERS.OF') }}
                {{ maintTotalPages }}
              </span>
              <Button
                variant="outline"
                size="xs"
                color="slate"
                :label="$t('SGP.MAINTENANCE.NEXT_PAGE')"
                :disabled="maintPage === maintTotalPages"
                @click="maintPage = Math.min(maintTotalPages, maintPage + 1)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
